/**
 * ÖSD Zertifikat C1 blog post: Academic Redemittel Schreiben & Sprechen (PDF).
 * Same structure as other posts: intro, table, sections, FAQ accordion.
 */
import React, { useState } from 'react';
import { ChevronDown, HelpCircle } from 'lucide-react';
import RelatedResources from './RelatedResources';
import SprechenSamplesCallout from './SprechenSamplesCallout';
import RedemittelPhraseTables from './RedemittelPhraseTables';

const BLOG_FAQ_ITEMS = [
  {
    question: 'What is the word count for ÖSD C1 Task 2?',
    answer: 'For Task 2 (the academic essay/report), you should write at least 250 words. Successful candidates often aim for 280–300 words to ensure they cover all argumentative nuances required for the higher score bands.',
  },
  {
    question: 'Is the ÖSD C1 certificate valid for university admission in Germany?',
    answer: 'Yes. The ÖSD Zertifikat C1 is widely recognized by universities in Austria, Germany, and Switzerland as proof of the language proficiency required for degree-seeking students.',
  },
  {
    question: 'How many tasks are in the ÖSD C1 Writing module?',
    answer: 'The writing module consists of two tasks: a shorter formal text (Task 1) and a longer argumentative or analytical essay (Task 2). You have a total of 90 minutes to complete both.',
  },
];

function BlogFaqItem({ question, answer, isOpen, onClick }) {
  return (
    <div className="group mb-4">
      <div
        className={`
          relative bg-white border-2 rounded-2xl cursor-pointer overflow-hidden
          transition-all duration-500
          ${isOpen
            ? 'border-black shadow-2xl scale-[1.02]'
            : 'border-black/10 hover:border-black/30 hover:shadow-xl hover:scale-[1.01]'
          }
        `}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onClick()}
        aria-expanded={isOpen}
      >
        <div className={`
          absolute inset-0 bg-gradient-to-br from-gray-50 via-white to-gray-50
          transition-all duration-500
          ${isOpen ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
        `} />
        <div className="relative z-10 p-6 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 flex-1">
            <div className={`
              relative flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center
              transition-all duration-500
              ${isOpen
                ? 'bg-black text-white scale-110 rotate-6'
                : 'bg-black/5 text-black group-hover:bg-black group-hover:text-white group-hover:scale-110'
              }
            `}>
              <HelpCircle className="w-6 h-6 relative z-10" />
            </div>
            <div className="relative flex-1">
              <h3 className={`
                font-space text-lg font-bold transition-all duration-300
                ${isOpen ? 'text-black' : 'text-black/80 group-hover:text-black'}
              `}>
                {question}
              </h3>
              <div className={`
                absolute bottom-0 left-0 h-0.5 bg-black transition-all duration-500
                ${isOpen ? 'w-full' : 'w-0 group-hover:w-1/3'}
              `} />
            </div>
          </div>
          <div className="flex-shrink-0">
            <ChevronDown className={`
              w-6 h-6 transition-all duration-500
              ${isOpen ? 'rotate-180 text-black scale-125' : 'rotate-0 text-black/50 group-hover:text-black group-hover:scale-110'}
            `} />
          </div>
        </div>
        <div className={`
          relative z-10 overflow-hidden transition-all duration-500
          ${isOpen ? 'max-h-[400px] opacity-100' : 'max-h-0 opacity-0'}
        `}>
          <div className="px-6 pb-6 pt-2">
            <div className="relative h-px bg-black/10 mb-4 overflow-hidden">
              <div className={`
                absolute inset-0 bg-gradient-to-r from-transparent via-black to-transparent
                transition-transform duration-1000
                ${isOpen ? 'translate-x-0' : '-translate-x-full'}
              `} />
            </div>
            <p className="text-black/70 leading-relaxed text-base md:text-lg max-w-none">
              {answer}
            </p>
          </div>
        </div>
        {isOpen && (
          <>
            <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-black/20 rounded-tl-2xl pointer-events-none" />
            <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-black/20 rounded-br-2xl pointer-events-none" style={{ animationDelay: '0.5s' }} />
          </>
        )}
      </div>
    </div>
  );
}

const OsdC1RedemittelPost = ({ pdfUrl }) => {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  return (
    <article className="blog-post-content">
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
        The ÖSD Zertifikat C1 is the premier certification for academic admission to universities in Austria and a recognized proof of high-level proficiency across the DACH region. At the C1 level, the exam shifts from communicative fluency to &quot;Wissenschaftssprache&quot; (academic language), requiring candidates to synthesize complex information and use a high-precision formal register.
      </p>

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Essential Redemittel for ÖSD C1: Synthesis & Academic Argumentation
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-4">
        The <em>Redemittel ÖSD C1</em> strategy is built around two primary tasks. Task 1 often involves a professional formal response or synthesis (e.g., a formal complaint or detailed inquiry), while Task 2 is an academic essay or report (Erörterung) requiring the analysis of data, graphics, or short texts.
      </p>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        For Task 2 (Academic Essay), you must use data-analysis phrases like &quot;Die vorliegende Statistik verdeutlicht, dass...&quot; or &quot;Auffallend ist in diesem Zusammenhang, dass...&quot;. For argumentation, connectors such as &quot;Nichtsdestotrotz sollte man in Betracht ziehen...&quot; or &quot;Infolgedessen lässt sich schlussfolgern...&quot; are mandatory to reach the C1 scoring threshold. Unlike B2, C1 examiners strictly penalize repetitive &quot;Alltagssprache&quot; (everyday language). Focusing on <em>Funktionsverbgefüge</em> (e.g., &quot;Zur Sprache bringen&quot; instead of &quot;sprechen&quot;) is the key to demonstrating academic readiness.
      </p>


      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-10 mb-4">
        Practising the Phrases Under Exam Conditions
      </h2>
      <p className="text-base md:text-lg text-black/70 leading-relaxed mb-6">
        A phrase list helps most when you can recall it under time pressure. Learn the phrases from the tables below
        or the free PDF, then use them in a timed answer. Logami is built by German teachers and has 6 complete ÖSD C1 mock exams
        in the official format, gives AI feedback on your written answers, and includes a Redemittel trainer that
        brings each phrase back just before you are likely to forget it. Your first mock exam is free.
      </p>

      {pdfUrl && (
        <p className="text-base md:text-lg text-black/70 leading-relaxed mb-8">
          If you want the complete collection of C1 academic phrases, you can download the PDF for the full Redemittel with examples for data analysis and academic essays.{' '}
          <a
            href={pdfUrl}
            download={pdfUrl.split('/').pop()}
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-black underline underline-offset-2 hover:opacity-80"
          >
            Download the full ÖSD C1 Redemittel PDF here
          </a>
          .
        </p>
      )}

      <RedemittelPhraseTables
        provider="osd"
        level="c1"
        examName="ÖSD C1"
        pdfUrl={pdfUrl}
      />

      <h2 className="font-space text-xl md:text-2xl font-bold text-black mt-12 mb-6">
        Frequently Asked Questions
      </h2>
      <div className="space-y-3">
        {BLOG_FAQ_ITEMS.map((item, index) => (
          <BlogFaqItem
            key={index}
            question={item.question}
            answer={item.answer}
            isOpen={openFaqIndex === index}
            onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
          />
        ))}
      </div>

      <p className="mt-10 pt-6 border-t border-black/10 text-sm text-black/50">
        <strong>Last Updated:</strong> October 7, 2026
      </p>
      <SprechenSamplesCallout provider="osd" level="c1" />
      <RelatedResources provider="osd" level="c1" variant="redemittel" />

    </article>
  );
};

export default OsdC1RedemittelPost;
